import { useCallback, useEffect, useState } from 'react'

import {
  getAdmins,
  getItemById,
  getProductDesignation,
} from '@/store/selectors'
import { logAmplitudeEvent } from '@/store/stats'
import { fetchAgents } from '@/store/agents'
import {
  Button,
  Heading,
  Modal,
  ModalPortal,
  SpacingToken,
  Text,
} from '@livechat/design-system-react-components'
import { css } from '@emotion/react'

import Divider from '@/components/Checkout/Wrapper/Divider'
import { StackedAgentList } from '@/components/AgentList/StackedAgentList'
import useParamSelector from '@/hooks/useParamSelector'
import { useTypedDispatch } from '@/store/hooks'
import type { IApplication } from '@/apis/apps/interfaces'
import { usePreventScroll } from '@/utils/browser'
import { useSendAppNotificationMutation } from '@/apis/emailinium/slice'
import { getNotificationAppInfo } from '@/utils/products'
import { notificationsCreate } from '@/store/notifications'
import { plural } from '@/utils/string'
import {
  AMPLITUDE_EVENTS,
  AppNotificationReason,
  NOTIFICATIONS,
} from '@/constants'
import { activeProduct } from '@/utils'

import PurchaseItem from './PurchaseModal/Content/PurchaseItem'

const modalCss = css`
  width: 95vw;
  max-width: 600px;
  max-height: calc(100% - var(${SpacingToken.Spacing8}));
  padding-block: var(${SpacingToken.Spacing6});

  .modal-content {
    overflow: hidden;
  }

  p {
    margin: 0;
  }

  > div {
    padding: 0 var(${SpacingToken.Spacing7});
  }
`
const modalHeading = css`
  margin: 0;
`
const footerCss = css`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;
  gap: var(${SpacingToken.Spacing2}) var(${SpacingToken.Spacing2});
`

const adminsHeaderCss = css`
  margin: 0 0 var(${SpacingToken.Spacing4});
`

interface Props {
  handleCloseModal: () => void
  appId: string
  appName: string
}

export const RequestInstallModal = ({ handleCloseModal, appId }: Props) => {
  const dispatch = useTypedDispatch()
  const [recipients, setRecipients] = useState<string[]>([])
  const designation = useParamSelector(getProductDesignation, appId)
  const agents = useParamSelector(getAdmins, designation)
  const item = useParamSelector(getItemById, appId)
  const [sendEmailNotification, { isLoading }] =
    useSendAppNotificationMutation()

  usePreventScroll()

  useEffect(() => {
    dispatch(fetchAgents.pending())
  }, [dispatch])

  const handleSendNotification = useCallback(async () => {
    dispatch(
      logAmplitudeEvent({
        name: AMPLITUDE_EVENTS.CONTACT_ADMIN_BUTTON_CLICKED,
        params: { appId, appName: item?.name },
      })
    )

    try {
      const response = await sendEmailNotification({
        apps: [
          getNotificationAppInfo({
            name: item.name,
            icon: item.icons?.small || '',
            slug: item.slug,
          }),
        ],
        recipients,
        marketplace: activeProduct,
        reason: AppNotificationReason.Installation,
      }).unwrap()

      if (response.code !== 'OK') {
        throw new Error()
      }

      dispatch(
        notificationsCreate({
          variant: NOTIFICATIONS.VARIANTS.SUCCESS,
          content: `Your ${plural(
            recipients.length,
            'admin has',
            'admins have'
          )} been notified.`,
          autoHideDelayTime: NOTIFICATIONS.AUTO_HIDE_DELAY_TIME,
        })
      )
      handleCloseModal()
    } catch (error) {
      dispatch(
        notificationsCreate({
          variant: NOTIFICATIONS.VARIANTS.ERROR,
          content:
            "Something went wrong and we couldn't send the notification. Please try again later or contact support.",
          autoHideDelayTime: NOTIFICATIONS.AUTO_HIDE_DELAY_TIME,
        })
      )
    }
  }, [
    dispatch,
    appId,
    item.name,
    item.icons?.small,
    item.slug,
    sendEmailNotification,
    recipients,
    handleCloseModal,
  ])

  return (
    <ModalPortal zIndex={1000}>
      <Modal
        css={modalCss}
        fullSpaceContent
        contentClassName="modal-content"
        data-test="modal"
        onClose={handleCloseModal}
        heading={
          <>
            <Heading size="sm" css={modalHeading}>
              You don't have an admin account
            </Heading>
            <Text size="sm">
              Only admins can install apps. You can notify one of them to
              request the installation instead.
            </Text>
          </>
        }
        footer={
          <div css={footerCss}>
            <Button kind="secondary" size="large" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              kind="primary"
              size="large"
              disabled={!recipients.length}
              onClick={handleSendNotification}
              loading={isLoading}
            >
              Send notification
            </Button>
          </div>
        }
      >
        <Divider fullWidth />
        <PurchaseItem item={item as IApplication} />
        <Divider />
        <Heading size="sm" css={adminsHeaderCss}>
          Who would you like to notify?
        </Heading>
        <StackedAgentList
          recipients={recipients}
          setRecipients={setRecipients}
          agents={agents}
        />
        <Divider fullWidth />
      </Modal>
    </ModalPortal>
  )
}
